/**
 * **Outbox terminal** — antrean penjualan yang menunggu diakui portal (K13 tier A · Fase 3 #5).
 *
 * ### ⭐⭐ Jalur luring dan daring adalah jalur yang SAMA
 * Penjualan **selalu** masuk antrean lebih dulu, lalu didorong. Internet cuma membuat dorongannya
 * seketika. ⇒ jalur ini dilewati **setiap transaksi**, jadi ia tak bisa membusuk diam-diam.
 * Mode luring yang hanya dipakai saat internet mati adalah mode luring yang **rusak justru saat
 * internet mati**.
 *
 * ### Kenapa `localStorage`, bukan IndexedDB
 * Sinkron (tak ada jendela di mana penjualan sudah "diterima layar" tapi belum tersimpan),
 * bertahan lintas muat-ulang, dan cukup: satu penjualan ≈ 1 KB, kuota ≈ 5 MB ⇒ ribuan penjualan.
 * IndexedDB menambah asinkronitas tepat di titik yang paling tak boleh punya celah.
 *
 * ⚠️ **Batas yang tak bisa dihapus** (`design/OUTLET-DB-ENGINE.md` §5c): tablet hilang/rusak
 * selagi antrean belum terkirim = penjualan itu hilang. DB lokal pun tak menolong — ia ikut
 * hilang bersama tabletnya. Yang mengurangi: dorong **tiap transaksi** (bukan tiap batch), dan
 * jumlah tertahan **selalu terlihat** di layar.
 *
 * ### ⭐ DUA jenis isi, satu antrean (2026-08-03)
 * | `kind` | Kapan | Ke mana |
 * |---|---|---|
 * | `order_batch` | **jaring**: `close` gagal JARINGAN di terminal ber-server. Pesanannya sudah hidup di portal; yang belum sampai cuma penutupannya | `/sync/up` (amplop per-kind) |
 * | `sale` | **tier satu-tablet (K13)**: penjualan disusun luring dan SELALU diantrekan, daring maupun tidak | `/sync/sale` (satu dokumen utuh) |
 *
 * ⛔ Sengaja **satu antrean**, bukan dua modul: dua antrean berarti dua penghitung, dua indikator,
 * dan dua tempat untuk lupa — sementara yang dijaga keduanya sama persis, yaitu uang yang sudah
 * masuk laci tapi belum sampai ke portal.
 */
import { api, ApiFailure } from './client';

/**
 * Kode galat dari server, atau `''` bila kegagalannya **transport** (belum pernah sampai).
 *
 * ⚠️ Perbedaannya menentukan nasib penjualan: yang belum pernah sampai wajib dicoba lagi, yang
 * sudah sampai dan ditolak tak akan berubah dengan dicoba lagi. `ApiFailure` menyimpan kodenya di
 * `detail.error_code` — membacanya di `.code` (seperti galat postgres) membuat SETIAP penolakan
 * server terbaca sebagai jaringan mati, dan antreannya mencoba selamanya. Sudah menipu saya sekali.
 */
function errorCode(e: unknown): string {
  return e instanceof ApiFailure ? e.detail.error_code : '';
}

const KEY = 'osbpos.outbox';

/**
 * ⛔ Ditolak server dengan alasan yang **tak akan berubah** kalau dicoba lagi.
 *
 * ⚠️⚠️ **Koreksi keputusan 2026-08-02.** Versi pertama modul ini MEMBUANG yang ditolak, dengan
 * alasan "antrean tersumbat selamanya oleh satu penjualan cacat". Alasannya sah, kesimpulannya
 * tidak — ia melewatkan pilihan ketiga. Membuang berarti **penjualan yang uangnya sudah diterima
 * lenyap tanpa jejak**, dan itu justru kerugian yang seluruh modul ini ada untuk mencegahnya.
 *
 * ⇒ Yang ditolak **ditahan** dengan tanda `needs_review`, dan **dilewati** saat mendorong. Antrean
 * tetap mengalir, penjualannya tetap ada, dan ia jadi pekerjaan yang terlihat.
 */
export type OutboxState = 'pending' | 'needs_review';

/** Satu penjualan selesai yang menunggu diakui portal. */
export interface OutboxItem {
  /**
   * ⚠️ Absen pada baris lama yang sudah telanjur ada di `localStorage` tablet ⇒ dibaca sebagai
   * `order_batch`. Migrasi diam-diam yang tak menghilangkan satu pun penjualan.
   */
  readonly kind?: 'order_batch' | 'sale';
  readonly state?: OutboxState;
  /** Alasan penolakan, APA ADANYA dari server. ⛔ Jangan diringkas jadi "gagal" — itu membuang
   *  satu-satunya petunjuk yang dipunya orang yang membukanya nanti. */
  readonly lastError?: string;
  /** ⭐ UUIDv7 dari KLIEN — identitas abadi (SYNC §1). Dipakai ulang di tiap percobaan kirim. */
  readonly recordId: string;
  /** ⛔ STABIL lintas percobaan. Kunci idempotensi portal; berubah = penjualan ganda. */
  readonly requestId: string;
  readonly outletCode: string;
  readonly deviceCode: string;
  readonly businessDate: string;
  readonly payload: Record<string, unknown>;
  /** Informasi saja — tak mengubah semantik apa pun. */
  attempt: number;
  readonly queuedAt: string;
}

function read(): OutboxItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw === null ? [] : (JSON.parse(raw) as OutboxItem[]);
  } catch {
    // ⚠️ Antrean rusak ⇒ JANGAN melempar. Layar kasir yang mati karena satu byte rusak lebih
    //    buruk daripada antrean yang kosong; yang hilang tetap terlihat lewat penghitung.
    return [];
  }
}

function write(items: readonly OutboxItem[]): void {
  localStorage.setItem(KEY, JSON.stringify(items));
}

/** Jumlah penjualan yang belum diakui portal. Ditampilkan terus di indikator kesehatan. */
export function pendingCount(): number {
  return read().filter((x) => x.state !== 'needs_review').length;
}

/**
 * ⭐ Yang tertahan karena DITOLAK server. Dipisah dari `pendingCount` dengan sengaja: yang satu
 * menunggu jaringan (akan beres sendiri), yang satu menunggu **manusia**. Menyatukannya membuat
 * angka yang tak pernah turun terlihat seperti jaringan yang sedang jelek.
 */
export function needsReviewCount(): number {
  return read().filter((x) => x.state === 'needs_review').length;
}

/** Dokumen yang butuh ditinjau — beserta alasannya. */
export function reviewItems(): readonly OutboxItem[] {
  return read().filter((x) => x.state === 'needs_review');
}

/**
 * ⭐⭐ Tier satu-tablet: menaruh SATU PENJUALAN UTUH ke antrean. **Sinkron** — begitu fungsi ini
 * kembali, penjualannya sudah aman dari muat-ulang, dan barulah layar boleh bilang "lunas".
 */
export function enqueueSale(doc: Record<string, unknown>): void {
  const recordId = String(doc['orderId'] ?? '');
  /* ⚠️ Idempoten di sisi klien juga: dua ketukan "bayar" tak boleh menggandakan antrean. */
  if (read().some((x) => x.recordId === recordId)) return;
  write([...read(), {
    kind: 'sale',
    state: 'pending',
    recordId,
    requestId: `sale-${recordId}`,
    outletCode: String(doc['outletCode'] ?? ''),
    deviceCode: String(doc['deviceCode'] ?? ''),
    businessDate: String(doc['businessDate'] ?? ''),
    payload: doc,
    attempt: 0,
    queuedAt: new Date().toISOString(),
  }]);
}

/**
 * Memasukkan penjualan ke antrean. **Sinkron dan selesai sebelum kembali** — begitu fungsi ini
 * kembali, penjualannya sudah aman dari muat-ulang.
 */
export function enqueue(item: Omit<OutboxItem, 'attempt' | 'queuedAt'>): void {
  write([...read(), { ...item, attempt: 0, queuedAt: new Date().toISOString() }]);
}

/** Hash isi — dipakai portal untuk mengenali `request_id` dipakai ulang untuk isi berbeda. */
async function payloadHash(records: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(records));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Mendorong antrean ke portal, **satu penjualan per amplop**.
 *
 * ### ⛔ Satu per amplop, bukan digabung
 * Amplop gabungan yang ditolak sebagian memaksa klien memutuskan nasib tiap record dari ringkasan
 * — dan tebakan di titik itu adalah tebakan tentang uang. Satu penjualan per amplop membuat
 * jawabannya selalu utuh: diterima, atau tidak.
 *
 * ⚠️⚠️ **Yang ditolak DITAHAN, bukan dibuang** — koreksi keputusan 2026-08-02; lihat catatan di
 * {@link OutboxState}. Ia ditandai `needs_review` lalu **dilewati**, jadi antrean tetap mengalir
 * tanpa satu pun penjualan lenyap.
 *
 * @returns berapa yang berhasil diakui, dan berapa yang jatuh ke peninjauan.
 */
export async function flush(): Promise<{ acked: number; rejected: number }> {
  /*
   * ⛔⛔ **Penjaga masuk-ulang.** Dorongan dipicu dari beberapa tempat sekaligus — tombol tutup,
   * efek berkala, dan `online` — dan tanpa penjaga ini dua dorongan bisa tumpang tindih lalu
   * mengirim dokumen yang SAMA dua kali.
   *
   * ⚠️ Idempotensi server memang menyelamatkannya (yang kedua dijawab `duplicate`), tapi itu bukan
   * alasan membiarkannya: ia membuang kuota tablet yang justru paling terbatas, dan ia menyandarkan
   * kebenaran pada penjagaan di seberang. Ketahuan dari uji OB4 yang menghitung DUA pengiriman
   * untuk satu penjualan.
   */
  if (flushing) return { acked: 0, rejected: 0 };
  flushing = true;
  let acked = 0;
  let rejected = 0;
  try {

  for (const item of read()) {
    /* ⛔ Yang menunggu MANUSIA dilewati — mencobanya lagi cuma menghasilkan penolakan yang sama. */
    if (item.state === 'needs_review') continue;

    /* ── tier satu-tablet: SATU dokumen penjualan utuh ────────────────────── */
    if (item.kind === 'sale') {
      try {
        await api.sendSale(item.payload);
        /*
         * ⭐ Termasuk saat server menjawab `duplicate: true` — itu bukan kegagalan, itu bukti
         * idempotensinya bekerja: dokumennya pernah sampai, jawabannya yang hilang.
         */
        acked += 1;
        drop(item.recordId);
      } catch (e) {
        const code = errorCode(e);
        /* ⛔ 5xx = server sedang bermasalah, BUKAN keputusan ⇒ tetap dicoba lagi. */
        if (code === '' || code.startsWith('HTTP-5')) {
          /* Kegagalan TRANSPORT — belum pernah sampai. Tetap antre, dan berhenti: jaringan mati. */
          bumpAttempt(item.recordId);
          break;
        }
        rejected += 1;
        hold(item.recordId, e instanceof Error ? e.message : String(e));
      }
      continue;
    }

    /* ── jaring terminal ber-server: amplop `order` lewat `/sync/up` ───────── */
    const records = [
      { id: item.recordId, revision: 1, business_date: item.businessDate, payload: item.payload },
    ];
    const envelope = {
      contract_version: '0.21.0',
      envelope_kind: 'up' as const,
      request_id: item.requestId,
      batch_id: item.recordId,
      endpoint: 'portal',
      payload_hash: await payloadHash(records),
      outlet_code: item.outletCode,
      device_code: item.deviceCode,
      lease_token: null,
      kind: 'order',
      schema_version: 1,
      record_count: 1,
      key_range: { from: item.recordId, to: item.recordId },
      business_date_range: { from: item.businessDate, to: item.businessDate },
      created_at: item.queuedAt,
      sent_at: new Date().toISOString(),
      attempt: item.attempt + 1,
      records,
    };

    try {
      const ack = await api.syncUp(envelope);
      if (ack.batch_result === 'rejected') {
        rejected += 1;
        hold(item.recordId, 'ditolak portal');
      } else {
        acked += 1;
        drop(item.recordId);
      }
    } catch {
      /*
       * ⚠️ Kegagalan TRANSPORT (jaringan, 5xx) ⇒ **tetap di antrean**, dan `attempt` naik.
       * Bedanya dengan ack `rejected` mutlak: yang satu belum pernah sampai, yang satu sudah
       * sampai dan ditolak. Memperlakukan keduanya sama berarti membuang penjualan yang cuma
       * kena jaringan berkedip.
       */
      bumpAttempt(item.recordId);
      break; // jaringan sedang mati — mencoba sisanya hanya membuang waktu
    }
  }

    return { acked, rejected };
  } finally {
    flushing = false;
  }
}

let flushing = false;

/** Menahan untuk ditinjau manusia. ⛔ Tak pernah menghapus — lihat {@link OutboxState}. */
function hold(recordId: string, reason: string): void {
  write(read().map((x) => (x.recordId === recordId
    ? { ...x, state: 'needs_review' as const, lastError: reason }
    : x)));
}

function drop(recordId: string): void {
  write(read().filter((x) => x.recordId !== recordId));
}

function bumpAttempt(recordId: string): void {
  write(read().map((x) => (x.recordId === recordId ? { ...x, attempt: x.attempt + 1 } : x)));
}

/** Hanya untuk uji & pemulihan ops. ⛔ Jangan pernah dipanggil dari alur kasir. */
export function clearOutbox(): void {
  localStorage.removeItem(KEY);
}
